import React, { useState, useEffect, useRef } from 'react';
import { COLORS } from '../../../../core/constants/colors';
import { webEnrichmentService } from '../../../web_enrichment/services/web-enrichment.service';
import { linkedInEnrichmentService } from '../../../linkedin_enrichment/services/linkedin-enrichment.service';

type EnrichmentType = 'web' | 'linkedin';
type ModalStep = 'form' | 'progress' | 'done' | 'error';

interface Props {
  type: EnrichmentType;
  companyId: number;
  initialUrl: string;
  onClose: () => void;
  onComplete: () => void;
}

/** Map raw API / network errors to friendly messages. */
function friendlyError(raw: string, type: EnrichmentType): string {
  const lower = raw.toLowerCase();

  if (lower.includes('api key not valid') || lower.includes('api_key_invalid'))
    return type === 'web'
      ? 'The web enrichment service is not configured correctly. Please contact your administrator.'
      : 'The LinkedIn enrichment service is not configured correctly. Please contact your administrator.';

  if (lower.includes('authentication') || lower.includes('auth'))
    return 'Authentication with the enrichment provider failed. Please check your API credentials.';

  if (lower.includes('rate limit') || lower.includes('429') || lower.includes('quota'))
    return 'The enrichment service is temporarily rate-limited. Please try again in a few minutes.';

  if (lower.includes('timeout') || lower.includes('timed out'))
    return 'The enrichment request timed out. The provider may be experiencing high load — please try again shortly.';

  if (lower.includes('already in progress') || lower.includes('409'))
    return 'An enrichment job is already running for this company. Please wait for it to finish.';

  if (lower.includes('not found') || lower.includes('404'))
    return type === 'linkedin'
      ? 'The LinkedIn company page could not be found. Please double-check the URL.'
      : 'The website could not be reached. Please verify the URL and try again.';

  if (lower.includes('disabled') || lower.includes('kill switch'))
    return 'This enrichment service is currently disabled by an administrator.';

  if (lower.includes('network') || lower.includes('fetch'))
    return 'A network error occurred. Please check your connection and try again.';

  // Fallback — strip HTTP noise but keep the core message
  return raw.replace(/^\d{3}\s*/, '').trim() || `Failed to start ${type} enrichment`;
}

export const EnrichmentActionModal: React.FC<Props> = ({
  type,
  companyId,
  initialUrl,
  onClose,
  onComplete,
}) => {
  const [url, setUrl] = useState(initialUrl);
  const [step, setStep] = useState<ModalStep>('form');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);
  const [progressLabel, setProgressLabel] = useState('Starting enrichment...');
  const [fieldsInfo, setFieldsInfo] = useState<{ extracted: number; total: number } | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollStartRef = useRef<number>(0);
  const POLL_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes max polling

  const title = type === 'web' ? 'Web Enrichment' : 'LinkedIn Enrichment';
  const urlLabel = type === 'web' ? 'Website URL' : 'LinkedIn URL';
  const urlPlaceholder =
    type === 'web' ? 'https://example.com' : 'https://linkedin.com/company/example';

  const icon =
    type === 'web' ? (
      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
        />
      </svg>
    ) : (
      <svg className="w-6 h-6" style={{ color: '#0077B5' }} fill="currentColor" viewBox="0 0 24 24">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    );

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const startPolling = () => {
    pollStartRef.current = Date.now();
    pollRef.current = setInterval(async () => {
      // Timeout — stop spinning after 5 minutes
      if (Date.now() - pollStartRef.current > POLL_TIMEOUT_MS) {
        stopPolling();
        setError('Enrichment is taking longer than expected. It may still be running in the background — check back shortly.');
        setStep('error');
        return;
      }

      try {
        const s = type === 'web'
          ? await webEnrichmentService.getStatus(companyId)
          : await linkedInEnrichmentService.getStatus(companyId);

        setProgressPercent(s.completion_percentage || 30);
        setFieldsInfo({ extracted: s.fields_extracted, total: s.total_possible_fields });

        if (s.status === 'completed') {
          stopPolling();
          setProgressPercent(100);
          setStep('done');
          onComplete();
        } else if (s.status === 'failed') {
          stopPolling();
          setError(friendlyError(s.error_message || 'Enrichment failed', type));
          setStep('error');
        } else {
          const label = type === 'web' ? 'Extracting data' : 'Extracting LinkedIn data';
          setProgressLabel(
            s.fields_extracted > 0
              ? `${label} (${s.fields_extracted}/${s.total_possible_fields} fields)...`
              : type === 'web' ? 'Analyzing website content...' : 'Scraping LinkedIn profile...'
          );
        }
      } catch {
        // Keep polling on transient errors
      }
    }, 3000);
  };

  const handleSubmit = async () => {
    if (!url.trim()) {
      setError(`Please enter a ${urlLabel.toLowerCase()}`);
      return;
    }

    setIsSubmitting(true);
    setError('');
    setProgressPercent(10);
    setProgressLabel(
      type === 'web' ? 'Submitting website for analysis...' : 'Triggering LinkedIn enrichment...'
    );

    try {
      if (type === 'web') {
        await webEnrichmentService.submitWebsite(companyId, url.trim(), true);
      } else {
        await linkedInEnrichmentService.triggerEnrichment(companyId, url.trim(), true);
      }
      setStep('progress');
      startPolling();
    } catch (err: any) {
      setError(friendlyError(err.message || '', type));
      setStep('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetry = () => {
    setStep('form');
    setError('');
    setProgressPercent(0);
    setFieldsInfo(null);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {icon}
            <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-all text-gray-500"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {/* Form Step */}
          {step === 'form' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">{urlLabel}</label>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={urlPlaceholder}
                className="w-full px-4 py-2.5 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
              />
              <p className="mt-2 text-xs text-gray-500">
                {type === 'web'
                  ? 'We will extract company information from this website using AI.'
                  : 'We will extract company profile data, leadership, and job openings from LinkedIn.'}
              </p>
              {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
            </div>
          )}

          {/* Progress Step */}
          {step === 'progress' && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin flex-shrink-0"
                  style={{ borderColor: COLORS.admin.primary, borderTopColor: 'transparent' }}
                />
                <p className="text-sm text-gray-700">{progressLabel}</p>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div
                  className="h-2 rounded-full transition-all duration-500"
                  style={{
                    width: `${progressPercent}%`,
                    backgroundColor: COLORS.admin.primary,
                  }}
                />
              </div>
              {fieldsInfo && (
                <p className="text-xs text-gray-500 text-right">
                  {fieldsInfo.extracted}/{fieldsInfo.total} fields extracted
                </p>
              )}
            </div>
          )}

          {/* Done Step */}
          {step === 'done' && (
            <div className="text-center py-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">{title} Complete</h3>
              <p className="text-sm text-gray-600 mb-3">
                {type === 'web'
                  ? `Successfully extracted data from ${url}`
                  : 'LinkedIn data has been extracted and is now available.'}
              </p>
              <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200 text-left">
                <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
                <p className="text-xs text-amber-800">
                  Extracted data is stored as <strong>suggestions</strong>. Open the{' '}
                  <strong>Manage Info</strong> tab to review any fields that differ from your
                  current values and choose whether to apply them.
                </p>
              </div>
            </div>
          )}

          {/* Error Step */}
          {step === 'error' && (
            <div className="text-center py-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">Enrichment Failed</h3>
              <p className="text-sm text-gray-600 mb-1">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          {step === 'form' && (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !url.trim()}
                className="px-5 py-2 text-sm font-medium text-white rounded-lg transition-all disabled:opacity-50"
                style={{ backgroundColor: COLORS.admin.primary }}
              >
                {isSubmitting ? 'Starting...' : 'Start Enrichment'}
              </button>
            </>
          )}
          {step === 'progress' && (
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 transition-all"
            >
              Run in Background
            </button>
          )}
          {step === 'error' && (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 transition-all"
              >
                Close
              </button>
              <button
                onClick={handleRetry}
                className="px-5 py-2 text-sm font-medium text-white rounded-lg transition-all"
                style={{ backgroundColor: COLORS.admin.primary }}
              >
                Try Again
              </button>
            </>
          )}
          {step === 'done' && (
            <button
              onClick={onClose}
              className="px-5 py-2 text-sm font-medium text-white rounded-lg transition-all"
              style={{ backgroundColor: COLORS.admin.primary }}
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
